/**
 * Single source of truth for the Telegram WebApp `initData` string.
 *
 * Why it's not in `auth.ts`: both `api.ts` (for `X-Init-Data` on
 * mutations) and `auth.ts` (for the initial `/v1/auth/telegram`
 * exchange) need it, and `auth.ts` already imports from `api.ts` —
 * pulling it back in the other direction would create a circular
 * import.
 *
 * The value is **always** read live from `window.Telegram.WebApp`, never
 * stored in `localStorage`. That's the entire premise of the JWT-binding
 * defense (see `apps/api/src/polybot_api/security/init_data_binding.py`):
 * a token lifted from `localStorage` is useless for mutations because
 * the matching initData isn't there to lift.
 *
 * Dev-mode override (`polybot.dev.initData`) lives in `localStorage`
 * because dev browsers have no Telegram WebApp host to read from. In
 * production that key is never set; in dev it's a deliberate
 * convenience that doesn't weaken the prod security model.
 */

import { tg } from './tg';

const FAKE_INIT_DATA_FLAG = 'polybot.dev.initData';

export function getInitData(): string {
  const real = tg.initData;
  if (real) return real;
  // Browser-cabinet path only (no Telegram host). `localStorage` access
  // throws in a private/blocked Safari — degrade to "no initData".
  try {
    return localStorage.getItem(FAKE_INIT_DATA_FLAG) ?? '';
  } catch {
    return '';
  }
}

/**
 * Same as ``getInitData`` but typed as the value the request layer
 * needs — empty string is treated as "no initData available", which
 * the backend will surface as a 401 binding error on mutations.
 */
export function getInitDataForRequest(): string {
  return getInitData();
}
